import cx from 'clsx';
import { getLegStatusColor } from 'common/helpers.ts';
import React from 'react';
import type { LEG_COLOR, LEG_STATUS } from 'types/Leg.ts';
import { ITruck } from './Icons.tsx';

// Tailwind cannot resolve interpolated class names, so the colour names returned by
// getLegStatusColor are mapped to fills here. getLegStatusColor stays the source of truth.
// Exhaustive over LEG_COLOR because the type requires it; no step resolves to red today, since a
// step is only asked for as delivered/transiting/pending and the legs2 view never yields cancelled.
const LEG_COLOR_FILLS: Record<LEG_COLOR, string> = {
  grey: 'bg-grey-light',
  aqua: 'bg-aqua',
  green: 'bg-green',
  red: 'bg-red',
};

const fillFor = (status: LEG_STATUS): string => LEG_COLOR_FILLS[getLegStatusColor(status)];

type Props = {
  className?: string;
  steps: string[];
  currentStep: string;
  onStepClick?: (x: string) => void;
  currentLegStatus?: LEG_STATUS;
  disableRing?: boolean;
};

export const Stepper: React.FC<Props> = ({ className, steps, currentStep, onStepClick, currentLegStatus, disableRing = false }) => {
  const currentStepIndex = steps.indexOf(currentStep);

  // A waypoint is reached once a leg has been delivered there, so currentStep is the last one reached.
  // The only exception is a pending first leg: it has not been collected yet, so nothing is reached.
  const stepIsReached = React.useCallback(
    (index: number) => {
      if (index > currentStepIndex) return false;
      if (currentLegStatus === 'pending' && currentStepIndex === 0) return false;
      return true;
    },
    [currentStepIndex, currentLegStatus],
  );

  // The segment being travelled right now is the one leading out of the last reached waypoint.
  const segmentIsInTransit = React.useCallback(
    (index: number) => currentLegStatus === 'transiting' && currentStepIndex >= 0 && index === currentStepIndex + 1,
    [currentStepIndex, currentLegStatus],
  );

  const onClick = (step: string) => () => {
    if (!onStepClick) return;
    onStepClick(step);
  };

  return (
    <ul className={cx('relative flex flex-row justify-items-start', className)}>
      {steps.map((step, index) => (
        <li key={index} className={cx('shrink basis-0', index !== 0 && 'flex-1', index === steps.length - 1 && 'justify-end')}>
          <div className="inline-flex min-h-7 w-full min-w-7 select-none items-center align-middle text-xs">
            <div
              className={cx(
                'relative h-1 w-full flex-1 text-white',
                !disableRing && 'hover:border-blue-500 hover:outline-hidden hover:ring',
                onStepClick && 'cursor-pointer',
                stepIsReached(index) ? fillFor('delivered') : fillFor('pending'),
              )}
              onClick={onClick(step)}
            >
              {segmentIsInTransit(index) && (
                <>
                  {/* the travelled half of the segment, up to the truck */}
                  <div className={cx('absolute top-1/2 h-1 w-1/2 -translate-y-1/2', fillFor('transiting'))} />
                  <div className="absolute top-1/2 left-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#d9f8ff] p-1">
                    <div className={cx('flex items-center justify-center rounded-full p-2', fillFor('transiting'))}>
                      <ITruck size={16} className="text-white" />
                    </div>
                  </div>
                </>
              )}
            </div>
            <span
              className={cx(
                'flex size-7 shrink-0 place-content-center place-items-center rounded-full text-lg text-white',
                stepIsReached(index) ? fillFor('delivered') : fillFor('pending'),
              )}
            >
              {step}
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
};
